import { type PropsWithChildren, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { useGetCurrentUser, useIsUserReady } from '@wecasa/auth/src/authentication-context';

import { CATEGORIES } from '@wecasa/common/src/constants/universes';
import { setBasketAttribute } from '@wecasa/common/src/ducks/common/basket';
import type { BasketState } from '@wecasa/common/src/ducks/customer/types';
import { ConfigContext } from '@wecasa/common/src/providers/config-provider';
import { useExperiment } from '@wecasa/common/src/providers/experiment-provider';
import { UniversesContext } from '@wecasa/common/src/providers/universes-provider';
import type {
  Address,
  BasketContent,
  CategoryReference,
  GiftCard,
  PrestationReference,
  ProGender,
  Universe,
  UniverseReference,
} from '@wecasa/common/src/types/commons';
import type { ReferringProParams } from '@wecasa/common/src/types/customer';
import { addOrRemovePrestation } from '@wecasa/common/src/utils/basket';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { getMessages } from '@wecasa/common/src/utils/errors';
import { createBasketContentFromPrestations } from '@wecasa/common/src/utils/prestations';
import { hasInvalidProChoices } from '@wecasa/common/src/utils/previous-pros';
import { isMassageReference } from '@wecasa/common/src/utils/universes';

import { useFetchGift } from '@wecasa/customer/src/queries/gifts-queries';

import useQuery from '@wecasa/web-ui/src/hooks/use-query';

import ErrorPage from '../../components/error-page/error-page';
import ducks from '../../ducks';
import type { RootState } from '../../store';
import { logoutUser, signInUser } from '../../utils/tracking';

export const GIFT_CARD_FETCH_TEST_ID = 'gift-card-data-loaded';

const initializeBasketContent = (category: CategoryReference, prestation: PrestationReference): BasketContent => [
  {
    reference: category,
    prestations: addOrRemovePrestation(0, prestation, true, [
      {
        reference: category,
        prestations: [],
      },
    ]),
  },
];

type StateProps = {
  basketUniverse: BasketState['universe'];
  proChoices: BasketState['proChoices'];
  basketContent: BasketState['basketContent'];
};

type DispatchProps = {
  setBasketContent: (basketContent: BasketContent) => void;
  resetBasket: (universe?: UniverseReference) => void;
  setBasketAddress: (address: Partial<Address>) => void;
  setProGender: (gender: ProGender) => void;
  setReferringProParams: (params: ReferringProParams) => void;
  resetPromotion: () => void;
  setBasketGiftCard: (giftCard: GiftCard) => void;
  setUniverses: (universes: Universe[]) => void;
};

export type LoaderProps = StateProps & DispatchProps & PropsWithChildren;

export const Loader = ({
  children,
  resetBasket,
  basketUniverse,
  setBasketContent,
  setBasketAddress,
  setProGender,
  setReferringProParams,
  proChoices,
  basketContent,
  resetPromotion,
  setBasketGiftCard,
  setUniverses,
}: LoaderProps) => {
  const {
    category,
    prestation,
    gift_code: giftCardCode,
    referring_pro: referringPro,
    referring_pro_name: referringProName,
    referring_pro_gender: referringProGender,
    universe,
  } = useQuery();
  const [initialized, setInitialized] = useState(false);
  const { loginExperimentUser, logoutExperimentUser } = useExperiment();
  const isUserReady = useIsUserReady();
  const user = useGetCurrentUser();
  const currentUniverse =
    import.meta.env.MODE === 'development' ? universe || window.wecasa.universe : window.wecasa.universe;
  const shouldFetchGiftCard = !!giftCardCode;
  const { isLoadingConfig, configError } = useContext(ConfigContext);
  const { isLoadingUniverses, universesError, universes } = useContext(UniversesContext);

  const { giftCard, giftCardError } = useFetchGift({
    // The return type of qs.useQuery for a query is string | string[] | ParsedQs | ParsedQs[] | undefined.
    // As we expect a string, we need to force the type
    code: giftCardCode as GiftCard['code'],
    options: { enabled: shouldFetchGiftCard },
  });
  const hasFetchedGift = !shouldFetchGiftCard || !!giftCard || !!giftCardError;

  useEffect(() => {
    if (universes) setUniverses(universes);
  }, [setUniverses, universes]);

  useEffect(() => {
    if (user?.public_id) {
      signInUser(user.public_id);
      loginExperimentUser({ userId: user.public_id, countryCode: getCurrentCountry() });
    } else {
      logoutUser();
      logoutExperimentUser();
    }
  }, [user, loginExperimentUser, logoutExperimentUser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount effect.
  useEffect(() => {
    if (hasInvalidProChoices(proChoices)) {
      resetBasket();
    }

    if (currentUniverse && basketUniverse !== currentUniverse) {
      resetBasket(currentUniverse as UniverseReference);
      if (category && prestation) {
        setBasketContent(initializeBasketContent(category as CategoryReference, prestation as PrestationReference));
      }

      if (isMassageReference(currentUniverse as UniverseReference)) {
        setProGender('dont_care');
      }
    } else if (basketContent.length === 0 && category && prestation) {
      setBasketContent(initializeBasketContent(category as CategoryReference, prestation as PrestationReference));
    }

    if (referringPro) {
      setReferringProParams({
        slug: referringPro as string,
        name: referringProName as string,
        gender: referringProGender as string,
      });
    }

    setInitialized(true);
  }, []);

  useEffect(() => {
    if (giftCardCode && giftCard && universes && 'zip_code' in giftCard) {
      const { zip_code: zipCode, universe: giftUniverse, prestations } = giftCard;

      setBasketGiftCard(giftCard);
      setBasketAddress({ zip_code: zipCode });

      // If there's already a promo, delete the promo.
      // As gift cards and promos cannot be used at the same time.
      resetPromotion();

      if (giftUniverse && giftUniverse === basketUniverse) {
        if (isMassageReference(giftUniverse) && prestations?.length) {
          setProGender('dont_care');
          /**
           * We always select the woman category as default.
           * A gift card prestation could be existing in the woman category and man category
           * at the same time. So the user would need to change the category manually in this case
           * on the categories screen.
           */
          setBasketContent([{ reference: CATEGORIES.MASSAGE_WOMAN, prestations: [...prestations] }]);
        } else if (prestations?.length) {
          setBasketContent(
            createBasketContentFromPrestations(
              prestations,
              // We expect the universe to be defined
              universes.find((u: Universe) => u.reference === giftUniverse)!,
            ),
          );
        }
      }
    }
  }, [
    giftCardCode,
    giftCard,
    universes,
    setBasketAddress,
    setBasketContent,
    setProGender,
    basketUniverse,
    resetPromotion,
    setBasketGiftCard,
  ]);

  if (!initialized || !isUserReady || isLoadingConfig || isLoadingUniverses) return null;

  if (universesError || configError || giftCardError) {
    return (
      <ErrorPage
        testId={hasFetchedGift ? GIFT_CARD_FETCH_TEST_ID : undefined}
        errors={[...getMessages(universesError), ...getMessages(configError), ...getMessages(giftCardError)]}
      />
    );
  }
  return <div data-testid={hasFetchedGift ? GIFT_CARD_FETCH_TEST_ID : undefined}>{children}</div>;
};

export const mapStateToProps = (state: RootState): StateProps => ({
  basketUniverse: state.basket.universe,
  proChoices: state.basket.proChoices,
  basketContent: state.basket.basketContent,
});

const mapDispatchToProps: DispatchProps = {
  setBasketContent: setBasketAttribute('basketContent'),
  setBasketAddress: setBasketAttribute('address'),
  setProGender: setBasketAttribute('proGender'),
  resetBasket: ducks.basket.reset,
  setReferringProParams: setBasketAttribute('referringProParams'),
  setBasketGiftCard: setBasketAttribute('giftCard'),
  resetPromotion: ducks.basket.resetPromotion,
  setUniverses: ducks.universes.set,
};

export default connect(mapStateToProps, mapDispatchToProps)(Loader);
